/**
 * Phase pill for the workspace center.
 *
 * Previously the pill was driven by a single ``replanning`` boolean set
 * from ``ai-explorer-page.tsx``. That flag over-fired post-plan-approval
 * (its trigger conditions matched both "follow-up turn waiting for v2
 * plan" AND "first turn's normal execution"), so the pill said
 * "Replanning…" while the agent was actually doing QA, synthesis, or
 * routine execution. Erik feedback 2026-05-21.
 *
 * This module reads the latest pill-worthy backend status event off the
 * deep-activity log and surfaces it as the pill label. The pill now
 * matches what's actually happening:
 *
 *   * "Replanning…" only during real replans
 *   * "Reviewing answer quality…" during QA
 *   * "Finalizing answer…" during synthesis retry
 *   * "Continuing todo execution…" during todo-stall recovery
 *   * nothing during quiet execution (the activity log + chip strip
 *     carry the signal in that phase; an extra pill is noise)
 *
 * Translation keys map to the labels the streaming layer (_legacy.py)
 * passes to ``_make_status_event``. The status event's
 * ``result_summary.label`` is the English fallback when no locale
 * translation is registered for the key.
 *
 * Lifecycle: the pill stays on the latest matching status until either
 * a newer matching status replaces it OR streaming ends OR the agent
 * run is reported inactive. The terminal markers ``deep_deliverable_ready``
 * / ``deep_plan_waiting_approval`` etc. are NOT pill-worthy — they
 * indicate transition into a stable state with dedicated UI elsewhere,
 * so emission of one of them implicitly clears whatever pill was active
 * (the next pill-worthy event is what shows). Streaming-end is the
 * hard clear.
 */

import type { DeepActivityEvent } from "@/stores/ai-explorer-store"
import type { TFunction } from "i18next"

/**
 * Translation keys whose status events drive the workspace phase pill.
 * Anything not in this set is ignored for pill purposes — including
 * lane-level chatter (``deep_explorer_thinking``).
 */
const _PILL_WORTHY_KEYS: ReadonlySet<string> = new Set([
  "deep_workflow_restart",
  "deep_plan_revising",
  "deep_quality_review_retry",
  "deep_synthesis_retry",
  "deep_todo_execution_retry",
])

/**
 * Translation keys whose status events CLEAR the pill — they mark the
 * agent transitioning into a stable state with dedicated UI elsewhere
 * (artifact card, approval bar, ask_user form). When one of these
 * fires AFTER a pill-worthy event, the pill should hide rather than
 * keep showing the stale transient phase.
 *
 * Without this clear-set, the pill would say "Reviewing answer
 * quality…" forever after qa_reviewer finished — the next
 * pill-worthy event in the next turn would never come (or come
 * much later), leaving the pill stuck.
 */
const _PILL_CLEAR_KEYS: ReadonlySet<string> = new Set([
  "deep_deliverable_ready",
  "deep_plan_waiting_approval",
  "deep_ask_user_waiting",
])

export interface WorkspacePhasePill {
  /** Localized label to render in the pill. */
  label: string
  /** Translation key — exposed for testing / telemetry. */
  translationKey: string
  /** Level forwarded from the status event ("info" or "error"). */
  level: "info" | "error"
}

/**
 * Pick the workspace-phase pill from the deep activity log.
 *
 * Returns ``null`` when there's no pill-worthy status (e.g. quiet
 * execution, no run, or only lane-level events). Pill clears at the
 * caller's discretion when streaming ends — this function only
 * reflects what's currently in the log.
 *
 * Scans backward to find the most recent matching status. The pill
 * does not implicitly time-out; if you want it cleared after a tool
 * runs to completion, emit a follow-up status from the backend.
 */
export function computeWorkspacePhasePill(
  activityLog: readonly DeepActivityEvent[] | undefined | null,
  t: TFunction
): WorkspacePhasePill | null {
  if (!activityLog || activityLog.length === 0) return null
  for (let i = activityLog.length - 1; i >= 0; i--) {
    const ev = activityLog[i]
    if (ev.event !== "status") continue
    // Workspace pill reflects the ORCHESTRATOR's phase, not a
    // subagent's. Subagent status events surface inside lane UI
    // (WorkerLanes) — they shouldn't drive the global pill.
    if (ev.source && ev.source !== "deep_explorer") continue
    const summary = ev.result_summary as
      | { label?: string; translation_key?: string; level?: string }
      | undefined
    if (!summary) continue
    const key = summary.translation_key
    if (!key) continue
    // Clear-set takes precedence: if the most-recent ORCHESTRATOR
    // status event is a transition-into-stable-state event, the pill
    // hides regardless of whatever pill-worthy event preceded it.
    // This is what stops the pill saying "Reviewing answer quality…"
    // forever after qa_reviewer finishes and ``deep_deliverable_ready``
    // fires.
    if (_PILL_CLEAR_KEYS.has(key)) return null
    if (!_PILL_WORTHY_KEYS.has(key)) continue
    const fallback = summary.label ?? ""
    const label = t(`deepExplorer.${key}`, { defaultValue: fallback })
    if (!label) continue
    const level: "info" | "error" = summary.level === "error" ? "error" : "info"
    return { label, translationKey: key, level }
  }
  return null
}

// Exported for tests so a regression that drops a key from the set
// fails loudly rather than silently making the pill go dark.
export const PILL_WORTHY_TRANSLATION_KEYS: ReadonlySet<string> = _PILL_WORTHY_KEYS
